import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { APIError, apiRequest } from "@core/services/api/apiRequest";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import { MarketingIntakeLayout } from "../../components/IntakeLayout";
import { getAttribution } from "../../utils/attribution";
import {
  affiliationPayload,
  asksForAffiliation,
  intakeFormQuery,
  type IntakeFormConfig,
} from "../IntakeForm";
import { intakeQuestionsFrom, packAnswers, type IntakeFormData } from "../IntakeForm";
import type { OptionType } from "@themes/pollinator/ui/Select";
import type { BookingDay, BookingSlot } from "../../components/BookingFlow";
import { MarketingIntakeWizard } from "./MarketingIntakeWizard";

type SlotsResponse = {
  times: BookingSlot[];
  scheduling_url: string;
};

/** A listed organization or role, as the public lookups return it. */
type NamedRecord = { id: number; name: string };

const toOption = (record: NamedRecord): OptionType => ({
  label: record.name,
  value: String(record.id),
  data: record,
});

/**
 * The server searches on nothing shorter. With nothing typed at all it offers
 * a few featured organizations, so the field opens onto something.
 */
const ORGANIZATION_QUERY_MIN_LENGTH = 2;

/** Long enough that a word typed at speed is one request, not one per letter. */
const ORGANIZATION_QUERY_DELAY_MS = 250;

type SubmitResponse = {
  confirmation_heading: string;
  confirmation_body: string;
  /** The quiz response the answers were stored as */
  quiz_response_id?: number | null;
};

export type Props = {
  formSlug: string;
  submissionSource?: string;
  className?: string;
};

/**
 * How far ahead to look for something to book. A meeting may be weeks away and
 * an info session a season away. The backend gathers the span a week at a
 * time, which is as much as Calendly answers for in one request, and looks no
 * further than 120 days (AVAILABILITY_HORIZON), so this is the same.
 */
const WINDOW_DAYS = 120;

function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Container for MarketingIntakeWizard.
 *
 * Availability is only asked for when the form actually books something, so a
 * participant filling in a waitlist form never waits on Calendly.
 */
export function MarketingIntakeWizardContainer({
  formSlug,
  submissionSource,
  className,
}: Props) {
  const [selectedTime, setSelectedTime] = useState<string>();
  const [confirmation, setConfirmation] = useState<{ heading: string; body: string }>();
  const [errorReason, setErrorReason] = useState<string>();

  const { data: config, isLoading } = useQuery<IntakeFormConfig>(
    intakeFormQuery(formSlug)
  );

  const usesAffiliation =
    !!config &&
    asksForAffiliation(
      [...config.required_fields, ...config.optional_fields],
      config.field_labels
    );

  // What is being typed into the organization select, and the same thing a
  // moment later, which is what gets searched for.
  const [organizationInput, setOrganizationInput] = useState("");
  const [organizationQuery, setOrganizationQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(
      () => setOrganizationQuery(organizationInput.trim()),
      ORGANIZATION_QUERY_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [organizationInput]);

  // These are the intake form's own lookups rather than the ones the profile
  // reads: nobody is signed in here, so they return only names staff have
  // listed, a screenful at a time: featured organizations before anything is
  // typed, then whatever matches.
  const { data: organizations } = useQuery<NamedRecord[]>({
    queryKey: ["intakeOrganizations", formSlug, organizationQuery],
    queryFn: () =>
      apiRequest(
        `/intake/${formSlug}/organizations/?q=${encodeURIComponent(organizationQuery)}`,
        "GET",
        undefined,
        false
      ),
    enabled:
      usesAffiliation &&
      (organizationQuery.length === 0 ||
        organizationQuery.length >= ORGANIZATION_QUERY_MIN_LENGTH),
    staleTime: 5 * 60 * 1000,
    // The last results stay up while the next letters are looked up, so the
    // list does not blink empty between keystrokes.
    placeholderData: keepPreviousData,
  });

  const { data: roles, isLoading: isLoadingRoles } = useQuery<NamedRecord[]>({
    queryKey: ["intakeRoles", formSlug],
    queryFn: () => apiRequest(`/intake/${formSlug}/roles/`, "GET", undefined, false),
    enabled: usesAffiliation,
    staleTime: 5 * 60 * 1000,
  });

  const organizationOptions = useMemo(
    () => (organizations ?? []).map(toOption),
    [organizations]
  );
  const roleOptions = useMemo(() => (roles ?? []).map(toOption), [roles]);

  const window = useMemo(() => {
    const start = new Date(Date.now() + 60_000);
    const end = new Date(start.getTime() + WINDOW_DAYS * 24 * 60 * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }, []);

  const {
    data: slots,
    isLoading: isLoadingTimes,
    isError: bookingUnavailable,
    refetch: refetchTimes,
  } = useQuery<SlotsResponse>({
    queryKey: ["intakeSlots", formSlug, window.start],
    queryFn: () =>
      apiRequest(
        `/intake/${formSlug}/slots/?start_time=${encodeURIComponent(
          window.start
        )}&end_time=${encodeURIComponent(window.end)}`,
        "GET",
        undefined,
        false
      ),
    retry: false,
    enabled: !!config?.books_a_meeting,
  });

  // Grouped in the visitor's own timezone: the day they are choosing is their
  // Tuesday, not Calendly's.
  const days: BookingDay[] = useMemo(() => {
    const grouped = new Map<string, BookingSlot[]>();
    (slots?.times ?? []).forEach((slot) => {
      const when = new Date(slot.start_time);
      const key = `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(when.getDate()).padStart(2, "0")}`;
      grouped.set(key, [...(grouped.get(key) ?? []), slot]);
    });
    return [...grouped.entries()].map(([date, daySlots]) => ({
      date,
      label: new Date(daySlots[0].start_time).toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }),
      slots: daySlots,
    }));
  }, [slots]);

  // The form's own questions, as its quiz publishes them.
  const questions = useMemo(
    () => intakeQuestionsFrom(config?.questions),
    [config?.questions]
  );

  const submit = useMutation({
    mutationFn: async (data: IntakeFormData) => {
      const response: SubmitResponse = await apiRequest(
        `/intake/${formSlug}/submit/`,
        "POST",
        {
          email: data.email,
          first_name: data.firstName ?? "",
          last_name: data.lastName ?? "",
          phone_number: data.phoneNumber ?? "",
          ...affiliationPayload(data),
          message: data.message ?? "",
          answers: packAnswers(questions, data.answers),
          submission_source: submissionSource ?? `apply_page:${formSlug}`,
          ...getAttribution(),
          booking_start_time: selectedTime ?? "",
          booking_timezone: browserTimezone(),
        },
        false
      );
      setConfirmation({
        heading: response?.confirmation_heading ?? "",
        body: response?.confirmation_body ?? "",
      });
      return { quizResponseId: response?.quiz_response_id };
    },
    onError: (error) => {
      const reason = error instanceof APIError ? String(error.data?.error ?? error.message) : "unknown";
      setErrorReason(reason);
      // Somebody else took the time while this visitor was answering. The
      // times on screen are the ones fetched when the page opened, so they are
      // fetched again: otherwise "Choose Another Time" offers the taken one.
      if (reason === "slot_taken") void refetchTimes();
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  // No such form, or its configuration could not be read. The page keeps its
  // frame, so the logo still leads home, and says where to go from here; a
  // bare sentence left the visitor with nothing to press.
  if (!config) {
    return (
      <MarketingIntakeLayout
        headline="This form is not available."
        mobileView="form"
        className={className}
      >
        <div
          className="flex flex-col items-start gap-4 px-1.5 lg:px-0 w-full max-w-[480px] mx-auto"
          data-testid="intake-unavailable"
        >
          <Type variant="h3" as="h2">
            This form is not available right now.
          </Type>
          <Type variant="body1">
            The link may be out of date, or we may be having trouble loading the
            form. Please try again in a moment.
          </Type>
          <Button as="a" href="/" variant="secondary" data-testid="intake-unavailable-home">
            Back to the Homepage
          </Button>
        </div>
      </MarketingIntakeLayout>
    );
  }

  return (
    <MarketingIntakeWizard
      formSlug={formSlug}
      audienceLabel={config.audience_label}
      headline={config.headline}
      introBody={config.intro_body}
      stepIntro={config.step_intro}
      submitLabel={config.submit_label}
      requiredFields={config.required_fields}
      optionalFields={config.optional_fields}
      fieldLabels={config.field_labels}
      extraFields={questions}
      formId={config.id}
      quizId={config.quiz_id ?? undefined}
      booksAMeeting={config.books_a_meeting}
      bookingStepStyle={config.booking_step_style}
      days={days}
      isLoadingTimes={isLoadingTimes}
      bookingUnavailable={bookingUnavailable}
      schedulingUrl={config.scheduling_url}
      selectedTime={selectedTime}
      onSelectTime={(time) => {
        setSelectedTime(time || undefined);
        setErrorReason(undefined);
      }}
      organizationOptions={organizationOptions}
      roleOptions={roleOptions}
      isLoadingRoles={isLoadingRoles}
      onOrganizationSearch={setOrganizationInput}
      confirmation={confirmation}
      errorReason={errorReason}
      onSubmit={submit.mutateAsync}
      className={className}
    />
  );
}

export default MarketingIntakeWizardContainer;
